import React, { useMemo } from 'react'

import {
	Controller,
	useFormContext,
	type FieldErrors,
} from 'react-hook-form'
import type {
	SelectProps as MUISelectProps,
	SelectChangeEvent as MUISelectChangeEvent,
} from '@mui/material/Select'
import MUISelect from '@mui/material/Select'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIFormControl from '@mui/material/FormControl'
import MUIFormHelperText from '@mui/material/FormHelperText'
import type { InputLabelProps as MUIInputLabelProps } from '@mui/material/InputLabel'
import type { FormControlProps as MUIFormControlProps } from '@mui/material/FormControl'

import { RFHControl } from '@/types'
import useFormField from '@/hooks/useFormField'

type ItemProps<T> = {
	disabled?: boolean
} & T

export type SelectChangeEvent<T> = MUISelectChangeEvent<T>

export type SelectProps<T> = {
	name?: string
	menuItems: ItemProps<T>[]
	children?: React.ReactNode
	labelField?: string
	valueField?: string
	control?: RFHControl
	errors?: FieldErrors
	helperText?: string
	selectProps?: Omit<MUISelectProps, 'disabled' | 'displayEmpty'>
	labelProps?: MUIInputLabelProps
	renderItem?: (item: ItemProps<T>) => React.ReactNode
	displayEmpty?: boolean
	label?: string
	placeholder?: string
	disabled?: boolean
	/**
	 * Whether the Select should be controlled by `react-hook-form`.
	 * When `true`, uses Controller wrapper. When `false`, renders as uncontrolled component.
	 * Defaults to `true`.
	 * */
	controlled?: boolean
} & MUIFormControlProps

/**
 * @deprecated This component is deprecated and will be removed in future releases.
 * Please use the new Select component from `@/components/select` or `@/components/select-controlled` to use with react-hook-form.
 */
const Select = <T,>({
	variant = 'outlined',
	name,
	selectProps,
	labelProps,
	label,
	placeholder,
	children,
	errors: propsErrors,
	control: propsControl,
	defaultValue,
	displayEmpty,
	menuItems,
	labelField = 'label',
	valueField = 'value',
	renderItem,
	helperText,
	disabled,
	controlled = true,
	...rest
}: SelectProps<T>) => {
	const formContext = useFormContext()
	const control = propsControl || formContext?.control
	const errors = propsErrors ?? formContext?.formState.errors

	const { getError } = useFormField()
	const error = getError(name, errors)
	const labelId = `${name || label}-label`

	const EmptyMenuItem = useMemo(
		() =>
			displayEmpty ? (
				<MUIMenuItem value="">
					<em>{placeholder || 'Select value'}</em>
				</MUIMenuItem>
			) : undefined,
		[displayEmpty, placeholder]
	)

	const MenuItems: React.ReactNode[] | undefined = useMemo(
		() =>
			menuItems
				? menuItems.map((item: ItemProps<T>, i) => {
						if (renderItem) {
							return renderItem(item)
						}

						return (
							<MUIMenuItem
								key={`${name}-menu-item-${i}`}
								value={
									item[valueField as keyof ItemProps<T>] as string
								}
								disabled={item.disabled}
							>
								{item[labelField as keyof ItemProps<T>] as string}
							</MUIMenuItem>
						)
					})
				: undefined,
		[labelField, menuItems, name, renderItem, valueField]
	)

	return (
		<MUIFormControl {...rest} variant={variant} error={!!error}>
			{label && (
				<MUIInputLabel {...labelProps} id={labelId}>
					{label}
				</MUIInputLabel>
			)}
			{control && name && controlled ? (
				<Controller
					name={name}
					control={control}
					defaultValue={defaultValue}
					render={({ field }) => {
						const handleSelectChange = (
							event: SelectChangeEvent<unknown>,
							child: React.ReactNode
						) => {
							field.onChange(event)
							selectProps?.onChange?.(event, child)
						}

						return (
							<MUISelect
								{...selectProps}
								{...field}
								onChange={handleSelectChange}
								disabled={disabled}
								labelId={labelId}
								label={label}
							>
								{EmptyMenuItem}
								{MenuItems}
								{children}
							</MUISelect>
						)
					}}
				/>
			) : (
				<MUISelect
					name={name}
					defaultValue={defaultValue}
					{...selectProps}
					disabled={disabled}
					labelId={labelId}
					label={label}
				>
					{EmptyMenuItem}
					{MenuItems}
					{children}
				</MUISelect>
			)}

			{!error && (
				<MUIFormHelperText>{helperText ?? ' '}</MUIFormHelperText>
			)}
			{error && (
				<MUIFormHelperText>{error.message}</MUIFormHelperText>
			)}
		</MUIFormControl>
	)
}

export default Select
